import {Expressions, getSystem, Plugins, Types} from '@packages/systems/wfdl';
import type {ExpressionPath, Type} from '@packages/systems/wfdl';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {isTypographic} from '@packages/utilities/elementTypeUtils';
import {getLocalizationKeyInElementData} from './localizationEditorUtils';

const CHILDREN_PATH: ExpressionPath = [{in: 'Record', at: 'children'}];

export function getLocalizationDetailsAtExpressionPath({
  element,
  path,
  dataType,
  plugins,
  walkUpToLocalizableParent = false,
}: {
  element: ExpressionElement;
  path: ExpressionPath;
  dataType: Type;
  plugins: Plugins.PluginsType;
  /**
   * When true, walk ancestors for a localizable Binding site and return the
   * split `bindingPath` / `writePath`. Default stays exact-path only so
   * EXPRESSIONS_CHANGED / ASSOC callers keep ancestor-unaware behavior.
   */
  walkUpToLocalizableParent?: boolean;
}):
  | {
      dataType: Type;
      existingLocalizationKey: string | null;
      bindingPath: ExpressionPath;
      writePath: ExpressionPath;
    }
  | undefined {
  const minLength = walkUpToLocalizableParent ? 0 : path.length;

  // Exact path first (length === path.length), then optionally walk up.
  for (let length = path.length; length >= minLength; length--) {
    const bindingPath = path.slice(0, length);
    const typeAtPath = Types.__DEPRECATED__getTypeAtExpressionPath(
      dataType,
      bindingPath,
      plugins
    );
    if (!typeAtPath) continue;

    const isLocalizableType = getSystem(typeAtPath)?.isLocalizable === true;
    const isTypographicChildren =
      Expressions.pathsEqual(bindingPath, CHILDREN_PATH) &&
      isTypographic(element);

    if (!isLocalizableType && !isTypographicChildren) continue;

    const writePath = path.slice(length);
    // Nested writes that cross Call (Append/plugin) are not relative to the
    // parent Binding value — e.g. Attributes Append under xattr. Walking up
    // would DomLoc-write Call paths into the loc list and wipe the editor.
    // Shorter prefixes only lengthen writePath, so Call stays forever — stop.
    if (
      writePath.length > 0 &&
      writePath.some((segment) => segment.in === 'Call')
    ) {
      return undefined;
    }

    return {
      dataType: typeAtPath,
      existingLocalizationKey: getLocalizationKeyInElementData(
        element,
        bindingPath
      ),
      bindingPath,
      writePath,
    };
  }

  return undefined;
}
